"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronRight, Copy, Terminal, Book, Server, Shield, Code, Zap, Key } from "lucide-react";

export default function ApiDocsPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col font-sans selection:bg-primary/20">
      {/* Topbar */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link href="/" className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/20">
                P
              </div>
              <span className="text-xl font-bold tracking-tight">PayTaara Developers</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
              <Link href="#" className="text-primary border-b-2 border-primary py-5">API Reference</Link>
              <Link href="#" className="text-muted-foreground hover:text-foreground transition-colors py-5">Guides</Link>
              <Link href="#" className="text-muted-foreground hover:text-foreground transition-colors py-5">SDKs</Link>
              <Link href="#" className="text-muted-foreground hover:text-foreground transition-colors py-5">Community</Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/dashboard">
              <Button variant="ghost" className="hidden sm:flex text-sm font-medium">Dashboard</Button>
            </Link>
            <Button className="rounded-full px-6 shadow-md hover:shadow-lg transition-all">Get API Keys</Button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-[1600px] w-full mx-auto px-4 md:px-8 flex flex-col md:flex-row relative">
        {/* Sticky Left Sidebar */}
        <aside className="w-full md:w-[260px] py-10 pr-8 hidden md:block shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto custom-scrollbar">
          <nav className="space-y-8">
            <div>
              <h4 className="font-bold text-xs uppercase tracking-widest text-muted-foreground mb-4">Getting Started</h4>
              <ul className="space-y-3 text-sm font-medium">
                <li><Link href="#introduction" className="text-primary flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-primary" /> Introduction</Link></li>
                <li><Link href="#authentication" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-transparent" /> Authentication</Link></li>
                <li><Link href="#errors" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-transparent" /> Errors</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-xs uppercase tracking-widest text-muted-foreground mb-4">Core Endpoints</h4>
              <ul className="space-y-3 text-sm font-medium">
                <li><Link href="#payouts" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-transparent" /> Payouts</Link></li>
                <li><Link href="#virtual-accounts" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-transparent" /> Virtual Accounts</Link></li>
                <li><Link href="#customers" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-transparent" /> Customers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-xs uppercase tracking-widest text-muted-foreground mb-4">Webhooks</h4>
              <ul className="space-y-3 text-sm font-medium">
                <li><Link href="#events" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-transparent" /> Event Types</Link></li>
                <li><Link href="#signatures" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-transparent" /> Verifying Signatures</Link></li>
              </ul>
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 py-10 md:pl-10 lg:pl-16 space-y-32 pb-32">
          
          {/* Introduction */}
          <section id="introduction" className="grid xl:grid-cols-[minmax(0,1fr)_450px] gap-12 xl:gap-24 items-start">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">API Reference</h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                The PayTaara API is organized around <strong className="text-foreground">REST</strong>. Our API has predictable resource-oriented URLs, accepts form-encoded request bodies, returns JSON-encoded responses, and uses standard HTTP response codes, authentication, and verbs.
              </p>
              
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 flex gap-4 mt-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
                <Shield className="h-6 w-6 text-primary shrink-0" />
                <div className="space-y-2">
                  <h4 className="font-bold text-foreground">Sandbox vs Live Mode</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    You can use the PayTaara API in test mode, which doesn't affect your live data or interact with banking networks. The API key you use to authenticate the request determines whether the request is live mode or test mode.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-950 rounded-2xl border border-zinc-800 p-6 flex flex-col justify-center text-zinc-400 space-y-4 xl:sticky xl:top-24 shadow-2xl">
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-amber-400" />
                <h3 className="text-white font-bold">Base URL</h3>
              </div>
              <p className="text-sm">All API requests should be made to our base URL.</p>
              <div className="bg-zinc-900 border border-zinc-700 px-4 py-3 rounded-lg flex justify-between items-center group cursor-pointer hover:border-zinc-500 transition-colors">
                <code className="text-emerald-400 text-sm font-mono">https://api.paytaara.com/v1</code>
                <Copy className="h-4 w-4 text-zinc-500 group-hover:text-white transition-colors" />
              </div>
            </div>
          </section>

          {/* Authentication */}
          <section id="authentication" className="grid xl:grid-cols-[minmax(0,1fr)_450px] gap-12 xl:gap-24 items-start border-t pt-16">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold tracking-tight">Authentication</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Authenticate your API requests by including your secret key in the Authorization header.
              </p>
              
              <div className="space-y-4 mt-8">
                <p className="text-muted-foreground">
                  All API requests must be made over <strong className="text-foreground">HTTPS</strong>. Calls made over plain HTTP will fail. API requests without authentication will also fail.
                </p>
                <div className="bg-muted/30 p-5 rounded-xl border space-y-3">
                  <div className="flex items-center gap-2 text-foreground font-bold">
                    <Key className="h-4 w-4 text-primary" /> Bearer Token
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Provide your secret key as a Bearer token in the <code className="bg-muted-foreground/20 px-1.5 py-0.5 rounded text-foreground font-mono text-xs">Authorization</code> header. You can manage your API keys in the Developer Dashboard.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl xl:sticky xl:top-24">
              <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-800 flex justify-between items-center text-zinc-400">
                <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">Authentication Example</span>
                <Copy className="h-4 w-4 cursor-pointer hover:text-white transition-colors" />
              </div>
              <div className="p-5 text-zinc-300 overflow-x-auto">
                <pre className="text-sm font-mono leading-relaxed">
                  <span className="text-emerald-400">curl</span> https://api.paytaara.com/v1/balance \<br/>
                  <span className="text-blue-400">  -H</span> <span className="text-amber-300">"Authorization: Bearer sk_test_123456789"</span>
                </pre>
              </div>
            </div>
          </section>

          {/* Payouts */}
          <section id="payouts" className="grid xl:grid-cols-[minmax(0,1fr)_450px] gap-12 xl:gap-24 items-start border-t pt-16">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-3xl font-bold tracking-tight">Create a Payout</h2>
                  <Badge className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 hover:text-blue-700 border-none px-2 py-0.5 text-xs font-bold shadow-none">POST</Badge>
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Send money directly to any supported bank account globally.
                </p>
                <div className="inline-flex items-center gap-3 bg-muted/30 px-4 py-2.5 rounded-lg border font-mono text-sm mt-2">
                  <span className="text-blue-600 font-bold">POST</span>
                  <span className="text-foreground font-medium">/v1/payouts</span>
                </div>
              </div>

              <div className="space-y-6 mt-8">
                <h3 className="text-xl font-bold border-b pb-2">Request Parameters</h3>
                <div className="space-y-6">
                  {/* Parameter Row */}
                  <div className="grid md:grid-cols-[150px_1fr] gap-4">
                    <div className="space-y-1">
                      <code className="font-bold text-foreground bg-muted px-1.5 py-0.5 rounded text-sm">amount</code>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono">integer</span>
                        <span className="text-[9px] font-bold text-destructive uppercase tracking-wider">Required</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Amount to send in the lowest denomination (e.g., kobo or cents). For example, <code>50000</code> = ₦500.00.
                    </p>
                  </div>
                  <div className="h-px bg-border/50" />
                  
                  {/* Parameter Row */}
                  <div className="grid md:grid-cols-[150px_1fr] gap-4">
                    <div className="space-y-1">
                      <code className="font-bold text-foreground bg-muted px-1.5 py-0.5 rounded text-sm">bank_code</code>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono">string</span>
                        <span className="text-[9px] font-bold text-destructive uppercase tracking-wider">Required</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      The 3-digit central bank code of the destination bank. You can fetch a list of supported banks via the <code className="bg-muted px-1 rounded text-xs text-foreground">/v1/banks</code> endpoint.
                    </p>
                  </div>
                  <div className="h-px bg-border/50" />

                  {/* Parameter Row */}
                  <div className="grid md:grid-cols-[150px_1fr] gap-4">
                    <div className="space-y-1">
                      <code className="font-bold text-foreground bg-muted px-1.5 py-0.5 rounded text-sm">account_number</code>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono">string</span>
                        <span className="text-[9px] font-bold text-destructive uppercase tracking-wider">Required</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      The recipient's 10-digit NUBAN account number.
                    </p>
                  </div>
                  <div className="h-px bg-border/50" />

                  {/* Parameter Row */}
                  <div className="grid md:grid-cols-[150px_1fr] gap-4">
                    <div className="space-y-1">
                      <code className="font-bold text-foreground bg-muted px-1.5 py-0.5 rounded text-sm">narration</code>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono">string</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Description of the transfer, visible to the recipient. Maximum 50 characters.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Code Snippets Right Column */}
            <div className="xl:sticky xl:top-24 space-y-4">
              <Tabs defaultValue="curl" className="w-full">
                <TabsList className="bg-transparent border-b border-border w-full justify-start rounded-none p-0 h-10 space-x-6">
                  <TabsTrigger value="curl" className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-0 h-full data-[state=active]:shadow-none font-medium">cURL</TabsTrigger>
                  <TabsTrigger value="node" className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-0 h-full data-[state=active]:shadow-none font-medium">Node.js</TabsTrigger>
                  <TabsTrigger value="python" className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-0 h-full data-[state=active]:shadow-none font-medium">Python</TabsTrigger>
                </TabsList>
                <TabsContent value="curl" className="mt-4">
                  <div className="bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl">
                    <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-800 flex justify-between items-center text-zinc-400">
                      <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">Request</span>
                      <Copy className="h-4 w-4 cursor-pointer hover:text-white transition-colors" />
                    </div>
                    <div className="p-5 text-zinc-300 overflow-x-auto">
                      <pre className="text-sm font-mono leading-relaxed">
{`curl -X POST https://api.paytaara.com/v1/payouts \\
  -H "Authorization: Bearer sk_test_12345" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 500000,
    "bank_code": "058",
    "account_number": "0123456789",
    "narration": "Payment for Q3 Invoice",
    "currency": "NGN"
  }'`}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="node" className="mt-4">
                  <div className="bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl">
                    <div className="p-5 text-zinc-300 overflow-x-auto">
                      <pre className="text-sm font-mono leading-relaxed">
{`const paytaara = require('paytaara')('sk_test_12345');

const payout = await paytaara.payouts.create({
  amount: 500000,
  bank_code: "058",
  account_number: "0123456789",
  narration: "Payment for Q3 Invoice",
  currency: "NGN"
});`}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl mt-6">
                <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-800 flex justify-between items-center text-zinc-400">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">Response</span>
                    <Badge className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 border-none px-1.5 py-0 text-[10px]">200 OK</Badge>
                  </div>
                  <Copy className="h-4 w-4 cursor-pointer hover:text-white transition-colors" />
                </div>
                <div className="p-5 text-zinc-300 overflow-x-auto">
                  <pre className="text-sm font-mono leading-relaxed text-emerald-300/90">
{`{
  "status": true,
  "message": "Payout initiated successfully",
  "data": {
    "id": "po_928374823",
    "reference": "tx_req_1002938",
    "amount": 500000,
    "fee": 5000,
    "status": "processing",
    "recipient": {
      "name": "JOHN DOE",
      "account_number": "0123456789",
      "bank_name": "Guaranty Trust Bank"
    },
    "created_at": "2023-10-24T12:00:00Z"
  }
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Virtual Accounts */}
          <section id="virtual-accounts" className="grid xl:grid-cols-[minmax(0,1fr)_450px] gap-12 xl:gap-24 items-start border-t pt-16">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-3xl font-bold tracking-tight">Create Virtual Account</h2>
                  <Badge className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 hover:text-blue-700 border-none px-2 py-0.5 text-xs font-bold shadow-none">POST</Badge>
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Provision a dedicated static bank account for your customer to receive inbound transfers.
                </p>
                <div className="inline-flex items-center gap-3 bg-muted/30 px-4 py-2.5 rounded-lg border font-mono text-sm mt-2">
                  <span className="text-blue-600 font-bold">POST</span>
                  <span className="text-foreground font-medium">/v1/virtual-accounts</span>
                </div>
              </div>

              <div className="space-y-6 mt-8">
                <h3 className="text-xl font-bold border-b pb-2">Request Parameters</h3>
                <div className="space-y-6">
                  {/* Parameter Row */}
                  <div className="grid md:grid-cols-[150px_1fr] gap-4">
                    <div className="space-y-1">
                      <code className="font-bold text-foreground bg-muted px-1.5 py-0.5 rounded text-sm">customer_id</code>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono">string</span>
                        <span className="text-[9px] font-bold text-destructive uppercase tracking-wider">Required</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      The unique ID of the customer you are provisioning the account for. Must be created prior to this call.
                    </p>
                  </div>
                  <div className="h-px bg-border/50" />
                  
                  {/* Parameter Row */}
                  <div className="grid md:grid-cols-[150px_1fr] gap-4">
                    <div className="space-y-1">
                      <code className="font-bold text-foreground bg-muted px-1.5 py-0.5 rounded text-sm">bvn</code>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono">string</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Customer's Bank Verification Number (Increases receiving limits on the account).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Code Snippets Right Column */}
            <div className="xl:sticky xl:top-24 space-y-6 mt-16 xl:mt-0">
              <div className="bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl">
                <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-800 flex justify-between items-center text-zinc-400">
                  <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">Request</span>
                  <Copy className="h-4 w-4 cursor-pointer hover:text-white transition-colors" />
                </div>
                <div className="p-5 text-zinc-300 overflow-x-auto">
                  <pre className="text-sm font-mono leading-relaxed">
{`{
  "customer_id": "cust_8273948",
  "bvn": "22334455667"
}`}
                  </pre>
                </div>
              </div>

              <div className="bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl">
                <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-800 flex justify-between items-center text-zinc-400">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">Response</span>
                    <Badge className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 border-none px-1.5 py-0 text-[10px]">200 OK</Badge>
                  </div>
                  <Copy className="h-4 w-4 cursor-pointer hover:text-white transition-colors" />
                </div>
                <div className="p-5 text-zinc-300 overflow-x-auto">
                  <pre className="text-sm font-mono leading-relaxed text-emerald-300/90">
{`{
  "status": true,
  "message": "Virtual account created",
  "data": {
    "account_number": "9988776655",
    "account_name": "PayTaara / Jane Smith",
    "bank_name": "Providus Bank",
    "currency": "NGN",
    "status": "active"
  }
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}
